/**
 * Display list → PDF (pdf-lib).
 *
 * Each page's content starts with one transform from page millimeters
 * (y down) to PDF points (y up), so every coordinate, pen width, dash and font
 * size below is written in millimeters exactly as laid out. Colors are written
 * in their own space (DeviceGray / DeviceCMYK / DeviceRGB) — no conversion.
 *
 * Fonts are embedded and subset (falling back to the full font when subsetting
 * fails, a known fontkit issue with some CFF fonts). Raster viewports arrive as
 * raw pixel buffers and become Flate-compressed image XObjects at their exact
 * paper size, so pixels-per-inch on paper is precisely the chosen DPI.
 */

import fontkit from '@pdf-lib/fontkit';
import {
  LineCapStyle,
  LineJoinStyle,
  PDFDocument,
  PDFName,
  PDFOperator,
  PDFOperatorNames,
  appendBezierCurve,
  beginText,
  closePath,
  concatTransformationMatrix,
  endPath,
  endText,
  fill,
  fillAndStroke,
  lineTo,
  moveTo,
  popGraphicsState,
  pushGraphicsState,
  rectangle,
  setCharacterSpacing,
  setDashPattern,
  setFillingCmykColor,
  setFillingGrayscaleColor,
  setFillingRgbColor,
  setFontAndSize,
  setLineCap,
  setLineJoin,
  setLineWidth,
  setStrokingCmykColor,
  setStrokingGrayscaleColor,
  setStrokingRgbColor,
  setTextMatrix,
  showText,
  stroke,
  type PDFFont,
  type PDFPage,
  type PDFRef,
} from 'pdf-lib';
import { MM_PER_PT, type PrintColor, type StrokeStyle } from 'shared-types';
import type { DisplayItem, DisplayPage, PathItem, TextItem } from '../displayList';

/** An image the pages reference by key. Raw buffers are row-major, top row first, 8 bits per channel. */
export type PdfImageResource = { kind: 'png' | 'jpeg'; bytes: Uint8Array } | { kind: 'raw'; width: number; height: number; channels: 1 | 3; data: Uint8Array; deflated?: boolean };

export interface PdfResources {
  fontBytes(key: string): Uint8Array | null;
  image(key: string): PdfImageResource | null;
}

export interface PdfMetadata {
  title?: string;
  author?: string;
  subject?: string;
  creator?: string;
}

const CAP: Record<StrokeStyle['cap'], LineCapStyle> = { butt: LineCapStyle.Butt, round: LineCapStyle.Round, square: LineCapStyle.Projecting };
const JOIN: Record<StrokeStyle['join'], LineJoinStyle> = { miter: LineJoinStyle.Miter, round: LineJoinStyle.Round, bevel: LineJoinStyle.Bevel };

function fillColor(color: PrintColor): PDFOperator {
  switch (color.space) {
    case 'gray':
      // PDF gray is lightness (1 = white); ours is ink coverage.
      return setFillingGrayscaleColor(1 - color.k);
    case 'cmyk':
      return setFillingCmykColor(color.c, color.m, color.y, color.k);
    case 'rgb':
      return setFillingRgbColor(color.r, color.g, color.b);
  }
}

function strokeColor(color: PrintColor): PDFOperator {
  switch (color.space) {
    case 'gray':
      return setStrokingGrayscaleColor(1 - color.k);
    case 'cmyk':
      return setStrokingCmykColor(color.c, color.m, color.y, color.k);
    case 'rgb':
      return setStrokingRgbColor(color.r, color.g, color.b);
  }
}

class PageWriter {
  private readonly ops: PDFOperator[] = [];
  private readonly fontNames = new Map<string, PDFName>();
  private readonly imageNames = new Map<string, PDFName>();

  constructor(
    private readonly page: PDFPage,
    private readonly fonts: (key: string) => Promise<PDFFont | null>,
    private readonly images: (key: string) => Promise<PDFRef | null>,
  ) {}

  async write(display: DisplayPage): Promise<void> {
    const k = 1 / MM_PER_PT;
    this.ops.push(pushGraphicsState(), concatTransformationMatrix(k, 0, 0, -k, 0, display.heightMm * k));
    await this.items(display.items);
    this.ops.push(popGraphicsState());
    this.page.pushOperators(...this.ops);
  }

  private async items(items: readonly DisplayItem[]): Promise<void> {
    for (const item of items) {
      switch (item.type) {
        case 'path':
          this.path(item);
          break;
        case 'text':
          await this.text(item);
          break;
        case 'image': {
          const name = await this.imageName(item.image);
          if (!name) break;
          this.ops.push(pushGraphicsState(), concatTransformationMatrix(item.w, 0, 0, -item.h, item.x, item.y + item.h), PDFOperator.of(PDFOperatorNames.DrawObject, [name]), popGraphicsState());
          break;
        }
        case 'group': {
          this.ops.push(pushGraphicsState());
          if (item.transform) this.ops.push(concatTransformationMatrix(...item.transform));
          if (item.clip) this.ops.push(rectangle(item.clip.x, item.clip.y, item.clip.w, item.clip.h), PDFOperator.of(PDFOperatorNames.ClipNonZero), endPath());
          await this.items(item.items);
          this.ops.push(popGraphicsState());
          break;
        }
      }
    }
  }

  private path(item: PathItem): void {
    if (!item.stroke && !item.fill) return;
    this.ops.push(pushGraphicsState());
    if (item.fill) this.ops.push(fillColor(item.fill));
    if (item.stroke) {
      this.ops.push(strokeColor(item.stroke.color), setLineWidth(item.stroke.weightMm), setLineCap(CAP[item.stroke.cap]), setLineJoin(JOIN[item.stroke.join]));
      if (item.stroke.dashMm.length > 0) this.ops.push(setDashPattern(item.stroke.dashMm, 0));
    }
    for (const c of item.commands) {
      switch (c.op) {
        case 'M':
          this.ops.push(moveTo(c.x, c.y));
          break;
        case 'L':
          this.ops.push(lineTo(c.x, c.y));
          break;
        case 'C':
          this.ops.push(appendBezierCurve(c.x1, c.y1, c.x2, c.y2, c.x, c.y));
          break;
        case 'Z':
          this.ops.push(closePath());
          break;
      }
    }
    const evenOdd = item.fillRule === 'evenodd';
    if (item.fill && item.stroke) this.ops.push(evenOdd ? PDFOperator.of(PDFOperatorNames.FillEvenOddAndStroke) : fillAndStroke());
    else if (item.fill) this.ops.push(evenOdd ? PDFOperator.of(PDFOperatorNames.FillEvenOdd) : fill());
    else this.ops.push(stroke());
    this.ops.push(popGraphicsState());
  }

  private async text(item: TextItem): Promise<void> {
    const font = await this.fonts(item.font);
    if (!font || !item.text) return;
    let name = this.fontNames.get(item.font);
    if (!name) {
      name = this.page.node.newFontDictionary(font.name, font.ref);
      this.fontNames.set(item.font, name);
    }
    const r = (item.rotationDeg * Math.PI) / 180;
    const cos = Math.cos(r);
    const sin = Math.sin(r);
    // Text space is y-up; flip it back against the page's y-down base transform.
    this.ops.push(
      pushGraphicsState(),
      fillColor(item.color),
      beginText(),
      setFontAndSize(name, item.sizeMm),
      setCharacterSpacing(item.letterSpacingMm),
      setTextMatrix(cos, sin, sin, -cos, item.x, item.y),
      showText(font.encodeText(item.text)),
      endText(),
      popGraphicsState(),
    );
  }

  private async imageName(key: string): Promise<PDFName | null> {
    const cached = this.imageNames.get(key);
    if (cached) return cached;
    const ref = await this.images(key);
    if (!ref) return null;
    const name = this.page.node.newXObject('Image', ref);
    this.imageNames.set(key, name);
    return name;
  }
}

export async function renderPdf(pages: readonly DisplayPage[], resources: PdfResources, metadata: PdfMetadata = {}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  if (metadata.title) doc.setTitle(metadata.title, { showInWindowTitleBar: true });
  if (metadata.author) doc.setAuthor(metadata.author);
  if (metadata.subject) doc.setSubject(metadata.subject);
  doc.setCreator(metadata.creator ?? 'Pointorama');
  doc.setProducer('Pointorama print engine (pdf-lib)');

  const fontCache = new Map<string, Promise<PDFFont | null>>();
  const fonts = (key: string): Promise<PDFFont | null> => {
    let pending = fontCache.get(key);
    if (!pending) {
      pending = (async () => {
        const bytes = resources.fontBytes(key);
        if (!bytes) return null;
        try {
          return await doc.embedFont(bytes, { subset: true });
        } catch {
          return doc.embedFont(bytes, { subset: false });
        }
      })();
      fontCache.set(key, pending);
    }
    return pending;
  };

  const imageCache = new Map<string, Promise<PDFRef | null>>();
  const images = (key: string): Promise<PDFRef | null> => {
    let pending = imageCache.get(key);
    if (!pending) {
      pending = (async () => {
        const resource = resources.image(key);
        if (!resource) return null;
        if (resource.kind !== 'raw') return (resource.kind === 'png' ? await doc.embedPng(resource.bytes) : await doc.embedJpg(resource.bytes)).ref;
        const dict = {
          Type: 'XObject',
          Subtype: 'Image',
          Width: resource.width,
          Height: resource.height,
          ColorSpace: resource.channels === 1 ? 'DeviceGray' : 'DeviceRGB',
          BitsPerComponent: 8,
        };
        const stream = resource.deflated ? doc.context.stream(resource.data, { ...dict, Filter: 'FlateDecode' }) : doc.context.flateStream(resource.data, dict);
        return doc.context.register(stream);
      })();
      imageCache.set(key, pending);
    }
    return pending;
  };

  for (const display of pages) {
    const widthPt = display.widthMm / MM_PER_PT;
    const heightPt = display.heightMm / MM_PER_PT;
    const page = doc.addPage([widthPt, heightPt]);
    page.setTrimBox(0, 0, widthPt, heightPt);
    await new PageWriter(page, fonts, images).write(display);
  }
  return doc.save();
}
